// ===================================================================
// Q&A / FELANMÄLAN (report-a-bug) PAGE
// ===================================================================

/**
 * @typedef {Object} ConfigType
 * @property {Object} ENDPOINTS
 * @property {string} ENDPOINTS.BASE - Backend base URL
 */

/** @type {ConfigType} */
const CONFIG = {
  ENDPOINTS: {
    BASE: "https://majema-free-copy-82691fc887b8a8bad1eb.o2.myshopify.dev",
  },
};

// UNCOMMENT WHEN DEV -> also first run ngrok
// CONFIG.ENDPOINTS.BASE = "https://7141-194-15-225-14.ngrok-free.app";

/** Path the bug report is POSTed to (backend route TBD). */
const REPORT_PATH = "/report-bug";

/** Max size per uploaded file (50 MB). */
const MAX_FILE_BYTES = 50 * 1024 * 1024;

/** @type {RegExp} */
const EMAIL_RE = /^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/;

/**
 * @param {number} bytes
 * @returns {string}
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Custom element handling the bug-report form: validation, file uploads and
 * submission to the backend.
 * @extends HTMLElement
 */
class QAndAForm extends HTMLElement {
  /** @type {HTMLFormElement|null} */
  #form = null;
  /** @type {HTMLElement|null} */
  #submitBtn = null;
  /** @type {HTMLInputElement|null} */
  #fileInput = null;
  /** @type {HTMLElement|null} */
  #dropzone = null;
  /** @type {HTMLElement|null} */
  #fileList = null;
  /** @type {HTMLElement|null} */
  #fileError = null;
  /** @type {HTMLElement|null} */
  #successBox = null;
  /** @type {HTMLElement|null} */
  #errorBox = null;
  /** @type {File[]} */
  #files = [];

  connectedCallback() {
    this.#form = this.querySelector(".qa__form");
    this.#submitBtn = this.querySelector(".qa__submit");
    this.#fileInput = this.querySelector(".qa__file-input");
    this.#dropzone = this.querySelector(".qa__dropzone");
    this.#fileList = this.querySelector(".qa__filelist");
    this.#fileError = this.querySelector(".qa__file-error");
    this.#successBox = this.querySelector(".qa__success");
    this.#errorBox = this.querySelector(".qa__error");

    if (!this.#form) return;

    this.#form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.#handleSubmit();
    });

    // Clear a field's error styling as the user fixes it.
    this.#form.querySelectorAll("[data-required]").forEach((el) => {
      ["input", "change"].forEach((evt) =>
        el.addEventListener(evt, () => this.#clearFieldError(el)),
      );
    });

    this.#initFileUpload();
  }

  // -----------------------------------------------------------------
  // FILE UPLOAD
  // -----------------------------------------------------------------
  #initFileUpload() {
    if (!this.#fileInput || !this.#dropzone) return;

    this.#dropzone.addEventListener("click", () => this.#fileInput?.click());
    this.#dropzone.addEventListener("keydown", (e) => {
      const ke = /** @type {KeyboardEvent} */ (e);
      if (ke.key === "Enter" || ke.key === " ") {
        e.preventDefault();
        this.#fileInput?.click();
      }
    });

    this.#fileInput.addEventListener("change", () => {
      this.#addFiles(this.#fileInput?.files);
    });

    ["dragenter", "dragover"].forEach((evt) =>
      this.#dropzone?.addEventListener(evt, (e) => {
        e.preventDefault();
        this.#dropzone?.classList.add("qa--dragover");
      }),
    );
    ["dragleave", "dragend", "drop"].forEach((evt) =>
      this.#dropzone?.addEventListener(evt, (e) => {
        e.preventDefault();
        this.#dropzone?.classList.remove("qa--dragover");
      }),
    );
    this.#dropzone.addEventListener("drop", (e) => {
      const de = /** @type {DragEvent} */ (e);
      this.#addFiles(de.dataTransfer?.files);
    });
  }

  /**
   * @param {FileList|null|undefined} fileList
   */
  #addFiles(fileList) {
    if (!fileList || !fileList.length) return;
    const rejected = [];
    for (const file of Array.from(fileList)) {
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(file.name);
        continue;
      }
      const isDuplicate = this.#files.some(
        (f) => f.name === file.name && f.size === file.size,
      );
      if (!isDuplicate) this.#files.push(file);
    }

    if (this.#fileError) {
      if (rejected.length) {
        this.#fileError.textContent = `Följande fil är för stor (max 50 MB): ${rejected.join(", ")}`;
        this.#fileError.classList.add("is-visible");
      } else {
        this.#fileError.classList.remove("is-visible");
      }
    }

    this.#syncFileInput();
    this.#renderFileList();
  }

  /**
   * @param {number} index
   */
  #removeFile(index) {
    this.#files.splice(index, 1);
    this.#syncFileInput();
    this.#renderFileList();
  }

  /** Keep the underlying <input type=file> in sync with this.#files. */
  #syncFileInput() {
    if (!this.#fileInput || typeof DataTransfer === "undefined") return;
    const dt = new DataTransfer();
    this.#files.forEach((f) => dt.items.add(f));
    this.#fileInput.files = dt.files;
  }

  #renderFileList() {
    if (!this.#fileList) return;
    this.#fileList.innerHTML = "";
    this.#files.forEach((file, index) => {
      const li = document.createElement("li");
      li.className = "qa__fileitem";

      const name = document.createElement("span");
      name.className = "qa__fileitem-name";
      name.textContent = file.name;

      const size = document.createElement("span");
      size.className = "qa__fileitem-size";
      size.textContent = formatFileSize(file.size);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "qa__fileitem-remove";
      remove.setAttribute("aria-label", `Ta bort ${file.name}`);
      remove.textContent = "×";
      remove.addEventListener("click", (e) => {
        e.stopPropagation();
        this.#removeFile(index);
      });

      li.append(name, size, remove);
      this.#fileList?.appendChild(li);
    });
  }

  // -----------------------------------------------------------------
  // VALIDATION
  // -----------------------------------------------------------------
  /**
   * @returns {boolean}
   */
  #validate() {
    if (!this.#form) return false;
    let isValid = true;
    /** @type {HTMLElement|null} */
    let firstInvalid = null;

    this.#form.querySelectorAll("[data-required]").forEach((node) => {
      const el = /** @type {HTMLInputElement|HTMLTextAreaElement} */ (node);
      const value = (el.value || "").trim();
      let fieldOk = value !== "";
      if (fieldOk && el.type === "email") fieldOk = EMAIL_RE.test(value);

      if (!fieldOk) {
        isValid = false;
        this.#setFieldError(el);
        if (!firstInvalid) firstInvalid = el;
      } else {
        this.#clearFieldError(el);
      }
    });

    if (firstInvalid) {
      /** @type {HTMLElement} */ (firstInvalid).scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      /** @type {HTMLElement} */ (firstInvalid).focus({ preventScroll: true });
    }
    return isValid;
  }

  /** @param {Element} el */
  #setFieldError(el) {
    el.classList.add("error-field");
    const msg = el.closest(".qa__field")?.querySelector(".qa__field-error");
    msg?.classList.add("is-visible");
  }

  /** @param {Element} el */
  #clearFieldError(el) {
    el.classList.remove("error-field");
    const msg = el.closest(".qa__field")?.querySelector(".qa__field-error");
    msg?.classList.remove("is-visible");
  }

  // -----------------------------------------------------------------
  // SUBMIT
  // -----------------------------------------------------------------
  async #handleSubmit() {
    if (!this.#form) return;
    this.#hideResult();
    if (!this.#validate()) return;

    this.#toggleLoading(true);

    const formData = new FormData();
    /** @type {Record<string,string>} */
    const fields = {
      description: ".qa__textarea[name='description']",
      device_type: "[name='device_type']",
      browser: "[name='browser']",
      browser_version: "[name='browser_version']",
      browser_zoom: "[name='browser_zoom']",
      name: "[name='name']",
      email: "[name='email']",
      phone: "[name='phone']",
    };
    for (const [key, selector] of Object.entries(fields)) {
      const el = /** @type {HTMLInputElement|null} */ (
        this.#form.querySelector(selector)
      );
      formData.append(key, el ? el.value : "");
    }
    this.#files.forEach((file) => formData.append("attachments[]", file));

    try {
      const response = await fetch(`${CONFIG.ENDPOINTS.BASE}${REPORT_PATH}`, {
        method: "POST",
        body: formData,
      });

      let responseData = null;
      try {
        responseData = await response.json();
      } catch {
        /* response may not be JSON */
      }

      if (response.ok) {
        this.#showSuccess();
      } else {
        this.#toggleLoading(false);
        const message =
          (responseData && responseData.error) ||
          response.statusText ||
          "Ett okänt fel uppstod";
        this.#showError(message, response.status);
      }
    } catch (error) {
      this.#toggleLoading(false);
      const err = /** @type {Error} */ (error);
      this.#showError(
        err.message ||
          "Nätverksfel. Kontrollera din internetanslutning och försök igen.",
        0,
      );
    }
  }

  /** @param {boolean} isLoading */
  #toggleLoading(isLoading) {
    this.#submitBtn?.classList.toggle("is-loading", isLoading);
    if (this.#submitBtn)
      /** @type {HTMLButtonElement} */ (this.#submitBtn).disabled = isLoading;
  }

  #showSuccess() {
    this.#toggleLoading(false);
    if (this.#form) this.#form.style.display = "none";
    this.#successBox?.classList.add("is-visible");
    this.#successBox?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  /**
   * @param {string} message
   * @param {number} [statusCode]
   */
  #showError(message, statusCode) {
    if (!this.#errorBox) return;
    const textEl = this.#errorBox.querySelector(".qa__result-text");
    if (textEl) textEl.textContent = message;
    const detailEl = this.#errorBox.querySelector(".qa__error-detail");
    if (detailEl) {
      if (statusCode && statusCode !== 200) {
        detailEl.textContent = `HTTP ${statusCode}`;
        /** @type {HTMLElement} */ (detailEl).style.display = "block";
      } else {
        /** @type {HTMLElement} */ (detailEl).style.display = "none";
      }
    }
    this.#errorBox.classList.add("is-visible");
    this.#errorBox.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  #hideResult() {
    this.#errorBox?.classList.remove("is-visible");
  }
}

customElements.define("q-and-a-form", QAndAForm);
